/**
 * Phase 711 — Email Center page.
 *
 * Full mailbox UI (Inbox / Sent / Drafts / Archive / Trash) backed by the
 * /api/email-center endpoints. Three-pane layout (folders | message list |
 * preview pane) plus Compose/Reply/Forward dialogs.
 *
 * NOTE: This module does NOT touch any existing email-sending logic.
 * Outgoing emails are mirrored into the Sent folder by the backend
 * `record_outbound_mail` helper, so this page just reads from the
 * mailbox collection.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
  Tray,
  PaperPlaneTilt,
  FileText,
  Archive,
  Trash,
  EnvelopeSimple,
  EnvelopeOpen,
  ArrowBendUpLeft,
  ArrowBendDoubleUpLeft,
  ArrowBendUpRight,
  Star,
  PencilSimple,
  MagnifyingGlass,
  Buildings,
  CaretRight,
  Paperclip,
  ArrowClockwise,
} from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const FOLDER_META = [
  { id: 'inbox', label_en: 'Inbox', label_el: 'Εισερχόμενα', Icon: Tray },
  { id: 'sent', label_en: 'Sent', label_el: 'Απεσταλμένα', Icon: PaperPlaneTilt },
  { id: 'drafts', label_en: 'Drafts', label_el: 'Πρόχειρα', Icon: FileText },
  { id: 'archive', label_en: 'Archive', label_el: 'Αρχείο', Icon: Archive },
  { id: 'trash', label_en: 'Trash', label_el: 'Κάδος', Icon: Trash },
];

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString('el-GR', { day: '2-digit', month: 'short' });
  } catch {
    return iso.slice(0, 10);
  }
};

const fmtFullDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('el-GR', {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatRecipients = (arr) => {
  if (!Array.isArray(arr) || arr.length === 0) return '';
  return arr.map(p => p.name ? `${p.name} <${p.email}>` : p.email).join(', ');
};

export default function EmailCenter() {
  const { language } = useLanguage();
  const t_label = (folder) => folder[language === 'el' ? 'label_el' : 'label_en'];

  const [activeFolder, setActiveFolder] = useState('inbox');
  const [folderCounts, setFolderCounts] = useState({});
  const [companyMailbox, setCompanyMailbox] = useState('info@cleanpro-ops.com');

  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [openMessage, setOpenMessage] = useState(null);
  const [thread, setThread] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [searchQ, setSearchQ] = useState('');
  const [searchActive, setSearchActive] = useState('');

  const [composeOpen, setComposeOpen] = useState(false);
  const [composeMode, setComposeMode] = useState('new'); // new | reply | replyAll | forward
  const [composeForm, setComposeForm] = useState({
    to: '', cc: '', bcc: '', subject: '', body_text: '',
    in_reply_to_message_id: null, forward_of_message_id: null,
  });

  // Phase 981 — Auto-save the in-progress compose form to localStorage
  // so an email is never lost when the user leaves Email Center to
  // fetch a file, hits Back, or reloads the page. Cleared only when
  // the user sends, explicitly saves as a real draft, or clicks
  // Discard.
  const DRAFT_STORAGE_KEY = 'emailcenter_working_draft_v1';
  const hasContent = (f) => (
    !!(f && (
      (f.to || '').trim() ||
      (f.cc || '').trim() ||
      (f.bcc || '').trim() ||
      (f.subject || '').trim() ||
      (f.body_text || '').trim() ||
      f.in_reply_to_message_id ||
      f.forward_of_message_id
    ))
  );

  // Restore on mount — if a working draft exists, re-open the composer
  // with it so the user can continue exactly where they stopped.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && parsed.form && hasContent(parsed.form)) {
        setComposeForm(parsed.form);
        setComposeMode(parsed.mode || 'new');
        setComposeOpen(true);
      }
    } catch (_e) { /* corrupt local storage — ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist every keystroke while the composer is open + has content.
  useEffect(() => {
    if (!composeOpen) return;
    try {
      if (hasContent(composeForm)) {
        localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ form: composeForm, mode: composeMode, saved_at: new Date().toISOString() }));
      } else {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    } catch (_e) { /* quota / disabled storage — ignore */ }
  }, [composeForm, composeMode, composeOpen]);

  const clearWorkingDraft = () => {
    try { localStorage.removeItem(DRAFT_STORAGE_KEY); } catch (_e) { /* ignore */ }
  };
  const discardDraft = () => {
    clearWorkingDraft();
    setComposeForm({ to: '', cc: '', bcc: '', subject: '', body_text: '', in_reply_to_message_id: null, forward_of_message_id: null });
    setComposeOpen(false);
    toast.success(language === 'el' ? 'Το πρόχειρο διαγράφηκε' : 'Draft discarded');
  };

  const fetchFolders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/email-center/folders`);
      const counts = {};
      (r.data?.folders || []).forEach(f => { counts[f.id] = f; });
      setFolderCounts(counts);
      if (r.data?.company_mailbox) setCompanyMailbox(r.data.company_mailbox);
    } catch (e) {
      // silent
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams();
      params.set('folder', activeFolder);
      if (searchActive) params.set('q', searchActive);
      const r = await axios.get(`${API}/api/email-center/messages?${params.toString()}`);
      setMessages(r.data?.messages || []);
    } catch (e) {
      toast.error('Αποτυχία φόρτωσης μηνυμάτων');
    } finally {
      setLoadingList(false);
    }
  }, [activeFolder, searchActive]);

  const fetchDetail = useCallback(async (msgId) => {
    if (!msgId) { setOpenMessage(null); setThread([]); return; }
    setLoadingDetail(true);
    try {
      const r = await axios.get(`${API}/api/email-center/messages/${msgId}`);
      setOpenMessage(r.data?.message || null);
      setThread(r.data?.thread || []);
      // Refresh folder counts (read state changed)
      fetchFolders();
    } catch (e) {
      toast.error('Αποτυχία ανοίγματος μηνύματος');
    } finally {
      setLoadingDetail(false);
    }
  }, [fetchFolders]);

  useEffect(() => { fetchFolders(); }, [fetchFolders]);
  useEffect(() => {
    fetchList();
    setSelectedId(null);
    setOpenMessage(null);
    setThread([]);
  }, [fetchList]);
  useEffect(() => { fetchDetail(selectedId); }, [selectedId, fetchDetail]);

  const onSearch = (e) => {
    e?.preventDefault?.();
    setSearchActive(searchQ.trim());
  };

  const onClearSearch = () => {
    setSearchQ('');
    setSearchActive('');
  };

  // --- Actions on the open message --------------------------------------
  const moveTo = async (folder) => {
    if (!openMessage) return;
    try {
      await axios.patch(`${API}/api/email-center/messages/${openMessage.message_id}`, { folder });
      toast.success(language === 'el' ? 'Μετακινήθηκε' : 'Moved');
      setSelectedId(null);
      fetchList();
      fetchFolders();
    } catch {
      toast.error('Αποτυχία');
    }
  };

  const toggleRead = async () => {
    if (!openMessage) return;
    try {
      await axios.patch(`${API}/api/email-center/messages/${openMessage.message_id}`, {
        is_read: !openMessage.is_read,
      });
      setOpenMessage({ ...openMessage, is_read: !openMessage.is_read });
      fetchList(); fetchFolders();
    } catch {
      toast.error('Αποτυχία');
    }
  };

  const deleteMsg = async () => {
    if (!openMessage) return;
    try {
      await axios.delete(`${API}/api/email-center/messages/${openMessage.message_id}`);
      toast.success(language === 'el' ? 'Μεταφέρθηκε στον κάδο' : 'Moved to trash');
      setSelectedId(null);
      fetchList(); fetchFolders();
    } catch {
      toast.error('Αποτυχία');
    }
  };

  const restoreMsg = async () => {
    if (!openMessage) return;
    try {
      await axios.post(`${API}/api/email-center/messages/${openMessage.message_id}/restore`);
      toast.success(language === 'el' ? 'Επαναφέρθηκε' : 'Restored');
      setSelectedId(null);
      fetchList(); fetchFolders();
    } catch {
      toast.error('Αποτυχία');
    }
  };

  const openCompose = (mode = 'new', srcMsg = null) => {
    if (mode === 'reply' && srcMsg) {
      setComposeForm({
        to: srcMsg.from_email || '',
        cc: '',
        bcc: '',
        subject: srcMsg.subject?.startsWith('Re:') ? srcMsg.subject : `Re: ${srcMsg.subject || ''}`,
        body_text: `\n\n--- ${language === 'el' ? 'Απάντηση σε' : 'On'} ${fmtFullDateTime(srcMsg.received_at || srcMsg.sent_at)} ${srcMsg.from_name || srcMsg.from_email} ${language === 'el' ? 'έγραψε' : 'wrote'}: ---\n${srcMsg.body_text || srcMsg.preview || ''}`,
        in_reply_to_message_id: srcMsg.message_id,
        forward_of_message_id: null,
      });
    } else if (mode === 'replyAll' && srcMsg) {
      const ccs = [
        ...(srcMsg.to || []).map(p => p.email),
        ...(srcMsg.cc || []).map(p => p.email),
      ].filter(e => e && e !== companyMailbox).join(', ');
      setComposeForm({
        to: srcMsg.from_email || '',
        cc: ccs,
        bcc: '',
        subject: srcMsg.subject?.startsWith('Re:') ? srcMsg.subject : `Re: ${srcMsg.subject || ''}`,
        body_text: `\n\n--- ${language === 'el' ? 'Απάντηση σε' : 'On'} ${fmtFullDateTime(srcMsg.received_at || srcMsg.sent_at)} ${srcMsg.from_name || srcMsg.from_email} ${language === 'el' ? 'έγραψε' : 'wrote'}: ---\n${srcMsg.body_text || srcMsg.preview || ''}`,
        in_reply_to_message_id: srcMsg.message_id,
        forward_of_message_id: null,
      });
    } else if (mode === 'forward' && srcMsg) {
      setComposeForm({
        to: '', cc: '', bcc: '',
        subject: srcMsg.subject?.startsWith('Fwd:') ? srcMsg.subject : `Fwd: ${srcMsg.subject || ''}`,
        body_text: `\n\n--- ${language === 'el' ? 'Προωθημένο μήνυμα' : 'Forwarded message'} ---\n${language === 'el' ? 'Από' : 'From'}: ${srcMsg.from_name || ''} <${srcMsg.from_email || ''}>\n${language === 'el' ? 'Ημερομηνία' : 'Date'}: ${fmtFullDateTime(srcMsg.received_at || srcMsg.sent_at)}\n${language === 'el' ? 'Θέμα' : 'Subject'}: ${srcMsg.subject || ''}\n\n${srcMsg.body_text || srcMsg.preview || ''}`,
        in_reply_to_message_id: null,
        forward_of_message_id: srcMsg.message_id,
      });
    } else {
      setComposeForm({
        to: '', cc: '', bcc: '', subject: '', body_text: '',
        in_reply_to_message_id: null, forward_of_message_id: null,
      });
    }
    setComposeMode(mode);
    setComposeOpen(true);
  };

  const submitCompose = async (asDraft = false) => {
    if (!composeForm.to.trim() && !asDraft) {
      toast.error(language === 'el' ? 'Απαιτείται παραλήπτης' : 'Recipient required');
      return;
    }
    try {
      const payload = {
        to: composeForm.to.split(',').map(s => s.trim()).filter(Boolean),
        cc: composeForm.cc.split(',').map(s => s.trim()).filter(Boolean),
        bcc: composeForm.bcc.split(',').map(s => s.trim()).filter(Boolean),
        subject: composeForm.subject,
        body_text: composeForm.body_text,
        in_reply_to_message_id: composeForm.in_reply_to_message_id,
        forward_of_message_id: composeForm.forward_of_message_id,
        save_as_draft: asDraft,
      };
      await axios.post(`${API}/api/email-center/compose`, payload);
      toast.success(asDraft
        ? (language === 'el' ? 'Αποθηκεύτηκε ως πρόχειρο' : 'Saved as draft')
        : (language === 'el' ? 'Στάλθηκε' : 'Sent'));
      // Phase 981 — Working draft is now persisted server-side, so clear
      // the local auto-save copy.
      clearWorkingDraft();
      setComposeForm({ to: '', cc: '', bcc: '', subject: '', body_text: '', in_reply_to_message_id: null, forward_of_message_id: null });
      setComposeOpen(false);
      // Switch user to Sent or Drafts folder to confirm immediately
      setActiveFolder(asDraft ? 'drafts' : 'sent');
      fetchList(); fetchFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Αποτυχία');
    }
  };

  // --- Render ----------------------------------------------------------
  const sidebar = (
    <aside className="flex flex-col w-56 bg-white border-r border-zinc-200" data-testid="email-center-sidebar">
      <div className="px-4 py-4 border-b border-zinc-200">
        <Button
          onClick={() => openCompose('new')}
          className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-semibold flex items-center gap-2"
          data-testid="email-center-compose-btn"
        >
          <PencilSimple size={16} weight="bold" />
          {language === 'el' ? 'Σύνταξη' : 'Compose'}
        </Button>
      </div>
      <nav className="flex-1 py-2">
        {FOLDER_META.map(f => {
          const counts = folderCounts[f.id] || { total: 0, unread: 0 };
          const isActive = activeFolder === f.id;
          const Icon = f.Icon;
          return (
            <button
              key={f.id}
              onClick={() => { setActiveFolder(f.id); onClearSearch(); }}
              className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                isActive ? 'bg-zinc-900 text-white font-semibold' : 'text-zinc-700 hover:bg-zinc-50'
              }`}
              data-testid={`email-center-folder-${f.id}`}
            >
              <span className="flex items-center gap-2">
                <Icon size={16} weight={isActive ? 'fill' : 'regular'} />
                {t_label(f)}
              </span>
              {counts.unread > 0 && (
                <span className={`text-[10px] font-mono px-1.5 py-0.5 ${
                  isActive ? 'bg-white text-zinc-900' : 'bg-zinc-900 text-white'
                }`} data-testid={`email-center-folder-${f.id}-unread`}>
                  {counts.unread}
                </span>
              )}
            </button>
          );
        })}
      </nav>
      <div className="px-4 py-3 border-t border-zinc-200 text-[11px] text-zinc-500">
        <div className="flex items-center gap-1">
          <EnvelopeSimple size={12} />
          <span className="truncate font-mono">{companyMailbox}</span>
        </div>
      </div>
    </aside>
  );

  const list = (
    <section className="flex flex-col w-96 border-r border-zinc-200 bg-zinc-50" data-testid="email-center-list">
      {/* Search bar */}
      <form onSubmit={onSearch} className="flex items-center gap-1 px-3 py-2 bg-white border-b border-zinc-200">
        <MagnifyingGlass size={14} className="text-zinc-400" />
        <Input
          value={searchQ}
          onChange={e => setSearchQ(e.target.value)}
          placeholder={language === 'el' ? 'Αναζήτηση…' : 'Search…'}
          className="rounded-none h-8 text-sm border-0 focus-visible:ring-0"
          data-testid="email-center-search-input"
        />
        {searchActive && (
          <button type="button" onClick={onClearSearch} className="text-[10px] text-zinc-500 hover:text-zinc-900" data-testid="email-center-clear-search">
            ✕
          </button>
        )}
        <button
          type="button"
          onClick={() => { fetchList(); fetchFolders(); }}
          className="p-1 text-zinc-500 hover:text-zinc-900"
          title={language === 'el' ? 'Ανανέωση' : 'Refresh'}
          data-testid="email-center-refresh"
        >
          <ArrowClockwise size={14} />
        </button>
      </form>

      <div className="flex-1 overflow-y-auto">
        {loadingList && (
          <div className="text-center text-xs text-zinc-400 py-8" data-testid="email-center-list-loading">
            {language === 'el' ? 'Φόρτωση…' : 'Loading…'}
          </div>
        )}
        {!loadingList && messages.length === 0 && (
          <div className="text-center text-xs text-zinc-400 py-12" data-testid="email-center-list-empty">
            {language === 'el' ? 'Κανένα μήνυμα.' : 'No messages.'}
          </div>
        )}
        {!loadingList && messages.map(m => {
          const isSelected = selectedId === m.message_id;
          const display = activeFolder === 'sent' || activeFolder === 'drafts'
            ? formatRecipients(m.to) || '(no recipient)'
            : (m.from_name || m.from_email || '(no sender)');
          return (
            <button
              key={m.message_id}
              onClick={() => setSelectedId(m.message_id)}
              className={`block w-full text-left px-3 py-2.5 border-b border-zinc-200 transition-colors ${
                isSelected ? 'bg-zinc-900 text-white' : (m.is_read ? 'bg-white hover:bg-zinc-50' : 'bg-white hover:bg-zinc-50')
              }`}
              data-testid={`email-center-list-item-${m.message_id}`}
            >
              <div className="flex items-center justify-between gap-2 mb-0.5">
                <span className={`text-xs truncate ${
                  isSelected ? 'text-white' : (m.is_read ? 'text-zinc-700 font-normal' : 'text-zinc-900 font-bold')
                }`}>
                  {display}
                </span>
                <span className={`text-[10px] font-mono whitespace-nowrap ${isSelected ? 'text-zinc-300' : 'text-zinc-400'}`}>
                  {fmtDateTime(m.received_at || m.sent_at || m.created_at)}
                </span>
              </div>
              <div className={`text-sm truncate ${
                isSelected ? 'text-white' : (m.is_read ? 'text-zinc-600' : 'text-zinc-900 font-semibold')
              }`}>
                {m.subject || '(no subject)'}
              </div>
              <div className={`text-[11px] truncate ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>
                {m.preview || ''}
              </div>
              {(m.attachments?.length > 0 || m.approval_id) && (
                <div className="flex items-center gap-2 mt-1">
                  {m.attachments?.length > 0 && (
                    <span className={`text-[10px] flex items-center gap-0.5 ${isSelected ? 'text-zinc-300' : 'text-zinc-400'}`}>
                      <Paperclip size={10} />
                      {m.attachments.length}
                    </span>
                  )}
                  {m.approval_id && (
                    <span className={`text-[10px] px-1 py-0.5 ${isSelected ? 'bg-white text-zinc-900' : 'bg-violet-100 text-violet-700'} font-mono`}>
                      APPROVAL
                    </span>
                  )}
                  {m.building_id && (
                    <span className={`text-[10px] flex items-center gap-0.5 ${isSelected ? 'text-zinc-300' : 'text-zinc-500'}`}>
                      <Buildings size={10} />
                    </span>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );

  const preview = (
    <section className="flex-1 flex flex-col bg-white overflow-hidden" data-testid="email-center-preview">
      {!openMessage && !loadingDetail && (
        <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
          {language === 'el' ? 'Επιλέξτε μήνυμα για προβολή.' : 'Select a message to preview.'}
        </div>
      )}
      {loadingDetail && (
        <div className="flex-1 flex items-center justify-center text-zinc-400 text-sm">
          {language === 'el' ? 'Φόρτωση…' : 'Loading…'}
        </div>
      )}
      {openMessage && !loadingDetail && (
        <>
          {/* Header */}
          <div className="border-b border-zinc-200 px-6 py-4">
            <div className="flex items-start justify-between gap-3 mb-2">
              <h2 className="text-lg font-bold text-zinc-900 leading-tight" data-testid="email-center-preview-subject">
                {openMessage.subject || '(no subject)'}
              </h2>
              <div className="flex items-center gap-1 flex-shrink-0">
                {activeFolder !== 'trash' && (
                  <>
                    <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => openCompose('reply', openMessage)} data-testid="email-center-reply-btn">
                      <ArrowBendUpLeft size={14} className="mr-1" />
                      {language === 'el' ? 'Απάντηση' : 'Reply'}
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => openCompose('replyAll', openMessage)} data-testid="email-center-reply-all-btn">
                      <ArrowBendDoubleUpLeft size={14} className="mr-1" />
                      {language === 'el' ? 'Απάντηση σε όλους' : 'Reply All'}
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-none h-8" onClick={() => openCompose('forward', openMessage)} data-testid="email-center-forward-btn">
                      <ArrowBendUpRight size={14} className="mr-1" />
                      {language === 'el' ? 'Προώθηση' : 'Forward'}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs text-zinc-600">
              <span className="font-semibold text-zinc-500">{language === 'el' ? 'Από:' : 'From:'}</span>
              <span data-testid="email-center-preview-from">{openMessage.from_name ? `${openMessage.from_name} ` : ''}&lt;{openMessage.from_email}&gt;</span>
              <span className="font-semibold text-zinc-500">{language === 'el' ? 'Προς:' : 'To:'}</span>
              <span data-testid="email-center-preview-to">{formatRecipients(openMessage.to) || '—'}</span>
              {(openMessage.cc?.length > 0) && (<>
                <span className="font-semibold text-zinc-500">Cc:</span>
                <span>{formatRecipients(openMessage.cc)}</span>
              </>)}
              <span className="font-semibold text-zinc-500">{language === 'el' ? 'Ημερομηνία:' : 'Date:'}</span>
              <span className="font-mono">{fmtFullDateTime(openMessage.received_at || openMessage.sent_at || openMessage.created_at)}</span>
              {openMessage.approval_id && (<>
                <span className="font-semibold text-zinc-500">Approval:</span>
                <a href={`/approvals/${openMessage.approval_id}`} className="text-violet-700 hover:underline" data-testid="email-center-preview-approval-link">
                  {openMessage.approval_id}
                </a>
              </>)}
              {openMessage.building_id && (<>
                <span className="font-semibold text-zinc-500">{language === 'el' ? 'Κτίριο:' : 'Building:'}</span>
                <a href={`/buildings/${openMessage.building_id}`} className="text-blue-700 hover:underline">
                  {openMessage.building_id}
                </a>
              </>)}
            </div>
            {/* Toolbar */}
            <div className="mt-3 flex items-center gap-1">
              <Button variant="outline" size="sm" className="rounded-none h-7" onClick={toggleRead} data-testid="email-center-toggle-read-btn">
                {openMessage.is_read ? <EnvelopeSimple size={14} className="mr-1" /> : <EnvelopeOpen size={14} className="mr-1" />}
                {openMessage.is_read
                  ? (language === 'el' ? 'Σήμανση ως μη αναγνωσμένο' : 'Mark Unread')
                  : (language === 'el' ? 'Σήμανση ως αναγνωσμένο' : 'Mark Read')}
              </Button>
              {activeFolder !== 'archive' && activeFolder !== 'trash' && (
                <Button variant="outline" size="sm" className="rounded-none h-7" onClick={() => moveTo('archive')} data-testid="email-center-archive-btn">
                  <Archive size={14} className="mr-1" />
                  {language === 'el' ? 'Αρχειοθέτηση' : 'Archive'}
                </Button>
              )}
              {activeFolder === 'trash' && (
                <Button variant="outline" size="sm" className="rounded-none h-7" onClick={restoreMsg} data-testid="email-center-restore-btn">
                  {language === 'el' ? 'Επαναφορά' : 'Restore'}
                </Button>
              )}
              {activeFolder !== 'trash' && (
                <Button variant="outline" size="sm" className="rounded-none h-7 text-red-600 hover:text-red-700" onClick={deleteMsg} data-testid="email-center-delete-btn">
                  <Trash size={14} className="mr-1" />
                  {language === 'el' ? 'Διαγραφή' : 'Delete'}
                </Button>
              )}
            </div>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {openMessage.body_html ? (
              <div
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: openMessage.body_html }}
                data-testid="email-center-preview-body-html"
              />
            ) : (
              <pre className="whitespace-pre-wrap text-sm text-zinc-800 font-sans" data-testid="email-center-preview-body-text">
                {openMessage.body_text || openMessage.preview || ''}
              </pre>
            )}

            {(openMessage.attachments?.length > 0) && (
              <div className="mt-6 border-t border-zinc-200 pt-4">
                <Label className="text-[11px] font-semibold uppercase text-zinc-500 mb-2 block">
                  {language === 'el' ? 'Συνημμένα' : 'Attachments'}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {openMessage.attachments.map((a, i) => (
                    <div key={a.attachment_id || i} className="flex items-center gap-2 px-3 py-2 border border-zinc-200 text-xs bg-zinc-50">
                      <Paperclip size={12} />
                      <span className="font-mono">{a.filename}</span>
                      <span className="text-zinc-400">({a.size ? Math.round(a.size/1024) + ' KB' : ''})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conversation Thread */}
            {thread.length > 1 && (
              <div className="mt-8 border-t border-zinc-200 pt-4" data-testid="email-center-thread">
                <Label className="text-[11px] font-semibold uppercase text-zinc-500 mb-3 block flex items-center gap-1">
                  <CaretRight size={12} />
                  {language === 'el' ? `Συνομιλία (${thread.length} μηνύματα)` : `Conversation (${thread.length} messages)`}
                </Label>
                <div className="space-y-2">
                  {thread.filter(m => m.message_id !== openMessage.message_id).map(m => (
                    <button
                      key={m.message_id}
                      onClick={() => setSelectedId(m.message_id)}
                      className="block w-full text-left border border-zinc-200 px-3 py-2 hover:bg-zinc-50 transition-colors"
                      data-testid={`email-center-thread-item-${m.message_id}`}
                    >
                      <div className="flex items-center justify-between text-[11px] text-zinc-500 mb-0.5">
                        <span className="font-semibold">
                          {m.direction === 'outbound' ? `→ ${formatRecipients(m.to)}` : `← ${m.from_name || m.from_email}`}
                        </span>
                        <span className="font-mono">{fmtFullDateTime(m.received_at || m.sent_at || m.created_at)}</span>
                      </div>
                      <div className="text-sm text-zinc-700 truncate">{m.subject}</div>
                      <div className="text-[11px] text-zinc-500 truncate">{m.preview}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </section>
  );

  return (
    <Layout>
      <div className="max-w-[1600px] mx-auto px-4 py-4" data-testid="email-center-page">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'var(--font-heading)' }}>
              <EnvelopeSimple size={22} weight="fill" className="text-zinc-700" />
              {language === 'el' ? 'Κέντρο Email' : 'Email Center'}
            </h1>
            <p className="text-xs text-zinc-500 mt-0.5">
              {language === 'el'
                ? `Εταιρική θυρίδα: ${companyMailbox}`
                : `Company mailbox: ${companyMailbox}`}
            </p>
          </div>
        </div>

        <div
          className="flex border border-zinc-200 bg-white"
          style={{ height: 'calc(100vh - 180px)', minHeight: '500px' }}
          data-testid="email-center-shell"
        >
          {sidebar}
          {list}
          {preview}
        </div>
      </div>

      {/* Compose / Reply / Forward Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="rounded-none max-w-3xl" data-testid="email-center-compose-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PencilSimple size={18} />
              {composeMode === 'reply' && (language === 'el' ? 'Απάντηση' : 'Reply')}
              {composeMode === 'replyAll' && (language === 'el' ? 'Απάντηση σε όλους' : 'Reply All')}
              {composeMode === 'forward' && (language === 'el' ? 'Προώθηση' : 'Forward')}
              {composeMode === 'new' && (language === 'el' ? 'Νέο μήνυμα' : 'New message')}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {language === 'el' ? 'Αποστολή ως' : 'Sending as'} <span className="font-mono">{companyMailbox}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase text-zinc-500">To</Label>
              <Input
                value={composeForm.to}
                onChange={e => setComposeForm({ ...composeForm, to: e.target.value })}
                placeholder="recipient@example.com, another@example.com"
                className="rounded-none mt-1 font-mono text-sm"
                data-testid="email-center-compose-to"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] uppercase text-zinc-500">Cc</Label>
                <Input
                  value={composeForm.cc}
                  onChange={e => setComposeForm({ ...composeForm, cc: e.target.value })}
                  className="rounded-none mt-1 font-mono text-sm"
                  data-testid="email-center-compose-cc"
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase text-zinc-500">Bcc</Label>
                <Input
                  value={composeForm.bcc}
                  onChange={e => setComposeForm({ ...composeForm, bcc: e.target.value })}
                  className="rounded-none mt-1 font-mono text-sm"
                  data-testid="email-center-compose-bcc"
                />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase text-zinc-500">{language === 'el' ? 'Θέμα' : 'Subject'}</Label>
              <Input
                value={composeForm.subject}
                onChange={e => setComposeForm({ ...composeForm, subject: e.target.value })}
                className="rounded-none mt-1"
                data-testid="email-center-compose-subject"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase text-zinc-500">{language === 'el' ? 'Μήνυμα' : 'Message'}</Label>
              <Textarea
                value={composeForm.body_text}
                onChange={e => setComposeForm({ ...composeForm, body_text: e.target.value })}
                rows={10}
                className="rounded-none mt-1 text-sm font-sans"
                data-testid="email-center-compose-body"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" className="rounded-none text-red-600 hover:bg-red-50 hover:text-red-700 mr-auto" onClick={discardDraft} data-testid="email-center-compose-discard">
              {language === 'el' ? 'Απόρριψη προχείρου' : 'Discard Draft'}
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => setComposeOpen(false)} data-testid="email-center-compose-cancel">
              {language === 'el' ? 'Κλείσιμο' : 'Close'}
            </Button>
            <Button variant="outline" className="rounded-none" onClick={() => submitCompose(true)} data-testid="email-center-compose-save-draft">
              {language === 'el' ? 'Αποθήκευση ως πρόχειρο' : 'Save Draft'}
            </Button>
            <Button className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white" onClick={() => submitCompose(false)} data-testid="email-center-compose-send">
              <PaperPlaneTilt size={14} className="mr-1" />
              {language === 'el' ? 'Αποστολή' : 'Send'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
